import React, { useCallback, useId, useState } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { VoucherForCampaignUserItemInput } from "@/lib/gql/__generated__/graphql";

import AddPhotoAlternativeIcon from "@/components/ui/icon/add-photo";

import { csvParser } from "./csv-utils";

type Props = {
	onSetCampaignList: (input: VoucherForCampaignUserItemInput[]) => void;
};

export const InputForm = React.forwardRef<HTMLInputElement, Props>(({ onSetCampaignList, ...props }, ref) => {
	const id = useId();
	const [isDragging, setIsDragging] = useState(false);

	const handleDragEnter = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLLabelElement>) => {
			e.preventDefault();

			const { files } = e.dataTransfer;

			if (!files || files.length <= 0) {
				return;
			}

			const file = files.item(0);
			setIsDragging(false);

			if (file) {
				csvParser(file).then((data) => {
					onSetCampaignList(data);
				});
			}
		},
		[onSetCampaignList],
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			e.preventDefault();
			const { files } = e.target;

			if (!files || files.length <= 0) {
				return;
			}

			const file = files.item(0);

			if (file) {
				csvParser(file).then((data) => {
					onSetCampaignList(data);
				});
			}
		},
		[onSetCampaignList],
	);

	return (
		<div className="relative w-full">
			<label
				htmlFor={id}
				className={cn(
					"z-10 block h-32 w-full max-w-[350px] rounded border border-zinc-200 p-4 hover:cursor-pointer", //
					isDragging && "bg-zinc-300/40",
				)}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
			>
				<div className="flex h-full w-full items-center justify-center text-zinc-500">
					<div className="flex flex-col gap-y-4">
						<div className="grid place-content-center">
							<AddPhotoAlternativeIcon className="h-10 w-10 text-zinc-500" />
						</div>
						<div className="flex flex-col gap-y-2">
							<p className="text-xs">クリックまたはドラッグ&ドロップでCSVファイルをアップロード</p>
							<p className="text-xs">
								<strong className="tracking-widest">.csv</strong>ファイルに対応しています
							</p>
						</div>
					</div>
				</div>
				<input
					type="file"
					accept=".csv"
					multiple
					id={id}
					ref={ref}
					onChange={handleChange}
					className="sr-only"
					{...props}
				/>
			</label>
		</div>
	);
});

InputForm.displayName = "InputForm";
